import "./styles/screen.scss";
import "./polyfills";
import "./utils/fixHoverTouch";
import noticeError from "./utils/noticeError";
import queryString from "query-string";
import { addSvgDefinitionsToBody } from "./utils/svgIcons";

// import everything from rxjs to make sure epics work as intended
import "rxjs";
import { setRequestInterceptorForDebugging } from "./utils/makeAjaxPromise";
import { LoggedInUser } from "./types/loggedInUser";
import { createRoot, Root } from "react-dom/client";
import { LoadingMessage } from "./components/ui/LoadingMessage";
import { InitializeAppDataFromServer } from "./types/InitializeAppDataFromServer";
import { LandingPage } from "./components/landingPage/LandingPage";
import NativeLoginForm from "./components/nativeApp/NativeLoginForm";
import { addAssetsPathsForFrontMatterPages } from "./addAssetsPathsForFrontMatterPages";
import "./configureOpenAPI";
import { InAppPurchaseSubscribeMessageInner } from "./inAppPurchases/InAppPurchaseSubscribeMessageInner";
import type { MksapApplication } from "./MksapApplication";
import { interceptRequestForAppWithSyncTesting } from "./utils/interceptRequestForAppWithSyncTesting";
import {
	LoggedInUserService,
	UserDetails as ServerUserDetails,
} from "./MksapAPI";
import { onDocumentReady } from "./utils/jQueryShim";
import { NativeAppInitializationError } from "./components/nativeApp/NativeAppInitializationError";

window.addEventListener("unhandledrejection", function (event: any) {
	event.preventDefault();
	noticeError(event.reason);
});

const documentReadyPromise = new Promise<void>((resolve) => {
	onDocumentReady(resolve);
});

let createRootPromise: Promise<Root> | undefined;
const getReactRoot = (): Promise<Root> => {
	if (!createRootPromise) {
		createRootPromise = documentReadyPromise.then(() => {
			const rootEl = document.getElementById("root")!;
			const root = createRoot(rootEl);
			return root;
		});
	}
	return createRootPromise;
};

function serverLoggedInUserDetailsToLoggedInUserDetails(
	serverData: ServerUserDetails,
): InitializeAppDataFromServer {
	// Some types are unknown on the server side, so we need to cast them here
	// to the correct types expected by the client.
	// TODO: Improve the OpenAPI spec to have correct types for these fields.
	return {
		...serverData,
		imIteResults: serverData.imIteResults as any,
		submissions: serverData.submissions as any,
	};
}

export async function startMksap() {
	try {
		validateBrowserSupport();

		const showLoadedMessageAndReturnRoot = getReactRoot().then((root) => {
			root.render(<LoadingMessage></LoadingMessage>);
			return root;
		});

		await reallyStartMksap({
			loggedInUserPromise: LoggedInUserService.getLoggedInUser().then(
				(r) => r.data,
			),
			dataFromServerPromise: LoggedInUserService.getLoggedInUserDetails().then(
				(r) => serverLoggedInUserDetailsToLoggedInUserDetails(r.data),
			),
			reactRootPromise: showLoadedMessageAndReturnRoot,
		});
	} catch (error) {
		await documentReadyPromise;

		document.body.innerHTML = `
			<div class="alert alert-danger">There was an error loading MKSAP. Please refresh the page shortly to try again.</div>
		`;
		throw error;
	}
}

export async function startMksapWithSyncDebugging() {
	const showLoadedMessageAndReturnRoot = getReactRoot().then((root) => {
		root.render(<LoadingMessage></LoadingMessage>);
		return root;
	});
	const loggedInUser = await LoggedInUserService.getLoggedInUser().then(
		(r) => r.data as LoggedInUser,
	);

	setRequestInterceptorForDebugging(interceptRequestForAppWithSyncTesting);

	await startMksapNative(loggedInUser, await showLoadedMessageAndReturnRoot);
}

export async function startMksapNative(userInfo: LoggedInUser, root: Root) {
	try {
		await reallyStartMksap({
			loggedInUserPromise: Promise.resolve(userInfo),
			dataFromServerPromise: Promise.resolve({}),
			reactRootPromise: Promise.resolve(root),
		});
	} catch (error) {
		console.log("Error starting MKSAP Native", error);
		root.render(<NativeAppInitializationError loggedInUser={userInfo} />);

		throw error;
	}
}

let mksapApplication: MksapApplication | undefined;
async function reallyStartMksap({
	loggedInUserPromise,
	dataFromServerPromise,
	reactRootPromise,
}: {
	loggedInUserPromise: Promise<LoggedInUser>;
	dataFromServerPromise: Promise<InitializeAppDataFromServer>;
	reactRootPromise: Promise<Root>;
}) {
	if (mksapApplication) {
		throw new Error("mksapApplication already initialized");
	}

	const [{ MksapApplication }, userInfo, dataFromServer, root] =
		await Promise.all([
			import(/* webpackChunkName: "MksapApplication" */ "./MksapApplication"),
			loggedInUserPromise,
			dataFromServerPromise,
			reactRootPromise,
		]);
	mksapApplication = new MksapApplication();
	(window as any).mksapApplication = mksapApplication;
	await mksapApplication.start(root, userInfo, dataFromServer);
}

function validateBrowserSupport(): void {
	if (!isBrowserSupported()) {
		onDocumentReady(() => {
			document.body.innerHTML = `
			<div style="margin:auto;padding:30px;text-align:center;width:700px;">
<p><strong>Please upgrade your browser to use ACP MKSAP</strong></p>
<p>We built ACP MKSAP using the latest techniques and technologies. This makes ACP MKSAP faster and easier to use. Unfortunately, your browser doesn't support those technologies.</p>
<p>Visit <a href="https://browsehappy.com/">https://browsehappy.com/</a>  to download or upgrade to the latest browsers.</p>
</div>
`;
		});
		throw new Error("browser isn't supported");
	}
}

function ensureEulaIsAccepted() {
	onDocumentReady(() => {
		const form = document.getElementById(
			"license-accept",
		) as HTMLFormElement | null;
		const checkbox = document.getElementById(
			"agreement-check",
		) as HTMLInputElement | null;
		const button = form
			? (form.querySelector("button") as HTMLButtonElement | null)
			: null;

		if (!form || !checkbox || !button) {
			console.warn("could not find eula acceptance form elements");
			return;
		}

		checkbox.addEventListener("change", function () {
			button.disabled = !checkbox.checked;
		});

		form.addEventListener("submit", function (event) {
			if (!checkbox.checked) {
				event.preventDefault();
			}
		});
	});
}

async function startLandingPage({
	showComingSoonMessage = false,
}: {
	showComingSoonMessage?: boolean;
} = {}) {
	const root = await getReactRoot();

	const onLogin = (_loggedInUser: LoggedInUser) => {
		const forwardPath = getForwardPath();

		window.location.pathname = forwardPath;
	};
	root.render(
		<LandingPage
			onLogIn={onLogin}
			showComingSoonMessage={showComingSoonMessage}
		/>,
	);

	function getForwardPath(): string {
		// When a user attempts to access a restricted page before logging in, the server redirects
		// to the login form, passing the path to the page they were trying to access in the `forward`
		// param in the query string.
		// This function returns that value, or "/app" if the param is not set.
		const queryParams = queryString.parse(window.location.search);
		if (typeof queryParams.forward === "string") {
			return queryParams.forward;
		} else {
			return "/app";
		}
	}
}

function isBrowserSupported(): boolean {
	if (window.CSS && CSS.supports("color", "var(--primary)")) {
		return true;
	} else {
		return false;
	}
}

async function startNativeAppLoginFormTest() {
	const root = await getReactRoot();

	const showLoginForm = () => {
		root.render(<NativeLoginForm onLogIn={onLogin} />);
	};

	const onLogin = (loggedInUser: LoggedInUser) => {
		// alert("login successful! the app would start now");
		// const loggedInUser: LoggedInUser = {
		// 	acpNumber: "00000000",
		// 	firstName: "Test",
		// 	lastName: "McTest",
		// 	username: "myusername",
		// 	uuid: "fakeuuid",
		// };
		root.render(
			<>
				<InAppPurchaseSubscribeMessageInner
					loggedInUser={loggedInUser}
					restorePurchases={async () => {
						alert("restoring purchases");
						await new Promise((resolve) => {
							setTimeout(resolve, 5000);
						});
					}}
					// showMismatchedAccountMessage
					product={{
						title: "ACP MKSAP 1 year",
						description: "One year of ACP MKSAP access",
						price: "$999.99",
						priceMicros: 999990000,
						currency: "USD",
						order: async () => {
							alert("ordering product");
							await new Promise((resolve) => {
								setTimeout(resolve, 5000);
							});
						},
					}}
					logOut={() => showLoginForm()}
				/>
				<div id="modal-root" />
			</>,
		);
	};
	showLoginForm();
}

onDocumentReady(() => {
	addAssetsPathsForFrontMatterPages();
});

export const MksapEntryPoints = {
	startMksap,
	startMksapWithSyncDebugging,
	startLandingPage,
	ensureEulaIsAccepted,
	startNativeAppLoginFormTest,
	addSvgDefinitionsToBody,
	addAssetsPathsForFrontMatterPages,
	startPreviewQuestionXml: () =>
		import(
			/* webpackChunkName: "PreviewQuestionXml" */ "./previewQuestionXml/PreviewQuestionXmlApp"
		).then((m) => m.startPreviewQuestionXml()),
};
(window as any).MksapEntryPoints = MksapEntryPoints;
